import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import ReactMarkdown from 'react-markdown'
import Button from './Button'
import Card, { CardActions, CardBody, CardHeader } from './Card'
import Select from './Select'
import Tag from './Tag'
import TextField, { InputLabel } from './TextField'

const statuses = {
  private: 'Marks your poosts as private',
  published: 'Marks your posts as published ',
}

const ENTER = 'Enter'

const CloseButton = (props) => (
  <button {...props} type="button">
    <svg
      className="w-4 h-4 opacity-50"
      fill="currentColor"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  </button>
)

const existsIn = (value, values) => values.indexOf(value) > -1

function TagEditor({ tags, onChange }) {
  const ref = useRef()
  const [values, setValues] = useState(tags || [])

  const changeValues = (newValues) => {
    setValues(newValues)
    onChange?.(newValues)
  }

  const keyPress = (e) => {
    if (e.key === ENTER) {
      e.preventDefault()
      let value = ref.current.value
      value = String(value).toLowerCase()
      if (values.indexOf(value) === -1) {
        changeValues([...values, value])
      }
      ref.current.value = ''
    }
  }

  const removeTag = (tag) => {
    changeValues(values.filter((f) => f !== tag))
  }

  return (
    <div>
      <InputLabel label="Tags" />
      <input
        type="text"
        ref={ref}
        className="w-full border-b border-gray-200 py-2 focus:outline-none"
        placeholder="Enter tags"
        onKeyPress={keyPress}
      />

      {values.length === 0 && (
        <div className="text-xs text-gray-400 mt-1 mb-2">
          Press ENTER after typing
        </div>
      )}
      <div className="space-x-1 mt-2">
        {values.map((tag, key) => (
          <Tag
            color={existsIn(tags, tag) ? 'gray' : 'blue'}
            key={key}
            radius="xl"
            action={<CloseButton onClick={() => removeTag(tag)} />}
          >
            {tag}
          </Tag>
        ))}
      </div>
    </div>
  )
}

export default function PostFormCard({ errors = {}, data = {}, onSubmit }) {
  const [title, setTitle] = useState(data.title || '')
  const [body, setBody] = useState(data.body || '')
  const [tags, setTags] = useState(data.tags || [])
  const { register, handleSubmit } = useForm({ defaultValues: data })

  errors = errors || {}

  const onType = (e) => setBody(e.target.value)
  const onTitleType = (e) => setTitle(e.target.value)

  const submit = (input) => {
    onSubmit({
      ...input,
      tags,
    })
  }

  return (
    <form onSubmit={handleSubmit(submit)} className="grid grid-cols-2 gap-3">
      <Card>
        <CardHeader title={data.id ? 'Edit post' : 'New post'} />

        <div className="space-y-4">
          <TextField
            name="title"
            error={errors.title}
            inputRef={register}
            placeholder="Title"
            label="Post title"
            autoComplete="off"
            onChange={onTitleType}
          />

          <TagEditor tags={data.tags || []} onChange={setTags} />

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <TextField
                name="slug"
                error={errors.slug}
                inputRef={register}
                placeholder="your-seo-friendly-title"
                label="Post slug"
                autoComplete="off"
              />
            </div>
            <Select
              label="Status"
              options={Object.keys(statuses)}
              name="status"
              inputRef={register}
            />
          </div>

          <TextField
            name="summary"
            error={errors.summary}
            inputRef={register}
            placeholder="Summary of your post"
            label="Post summary (auto-generated if empty)"
            multiline
            autoComplete="off"
          />
          <TextField
            name="body"
            error={errors.body}
            inputRef={register}
            placeholder="Body "
            label="Post body"
            multiline
            rows={10}
            autoComplete="off"
            onChange={onType}
          />
        </div>
        <CardActions>
          <Button type="submit" color="purple">
            Submit
          </Button>
        </CardActions>
      </Card>

      <Card>
        <CardBody>
          <div className="text-3xl font-bold text-gray-800 py-1">{title}</div>
          <ReactMarkdown className="markdown-body">{body}</ReactMarkdown>
        </CardBody>
      </Card>
    </form>
  )
}

PostFormCard.fragments = {
  post: `#graphql
    fragment PostFormCard on Post {
      id
      title
      slug
      body
      status
      summary
      insertedAt
      updatedAt
      tags
    }
  `,
}
